import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import StatsEliteLogo from "../brand/StatsEliteLogo";
import AuthSliderPanel from "./AuthSliderPanel";
import "../../components/admin/Admin.css";

const UserLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const { userLogin, userRegister } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was redirected from consultation request
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    if (redirect === "consultation") {
      setError("Please login or register to request a consultation.");
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let result;
      if (isRegister) {
        result = await userRegister(name, email, password);
      } else {
        result = await userLogin(email, password);
      }

      if (result.success) {
        // Check user role from localStorage to determine redirect
        setTimeout(() => {
          const userStr =
            localStorage.getItem("user") || localStorage.getItem("admin_user");
          if (userStr) {
            const user = JSON.parse(userStr);
            const userRole = user?.role;

            if (
              userRole === "admin" ||
              userRole === "editor" ||
              userRole === "viewer"
            ) {
              navigate("/admin/dashboard", { replace: true });
            } else {
              // Regular user - check for redirect parameter
              const params = new URLSearchParams(location.search);
              const redirect = params.get("redirect") || "/";
              navigate(redirect, { replace: true });
            }
          } else {
            // Fallback to home or redirect
            const params = new URLSearchParams(location.search);
            const redirect = params.get("redirect") || "/";
            navigate(redirect, { replace: true });
          }
        }, 100);
      } else {
        setError(result.message);
        setLoading(false);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsRegister(!isRegister);
    setError("");
    setEmail("");
    setPassword("");
    setName("");
  };

  return (
    <div className="auth-shell">
      <AuthSliderPanel
        eyebrow={isRegister ? "Create Account" : "Client Access"}
        title="Turn data requests into guided conversations."
        text="Sign in to request consultations and stay connected with Stats Elite advisory support."
      />
      <div className="auth-panel">
        <div className="auth-card">
        {/* Logo & Title */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "14px",
            }}
          >
            <StatsEliteLogo markSize={58} wordSize={22} gap={10} />
          </div>
          <p
            style={{
              fontSize: "14px",
              color: "#666666",
              fontWeight: "400",
              margin: "0",
            }}
          >
            {isRegister ? "Create your account" : "Welcome back"}
          </p>
        </div>

        {error && (
          <div
            style={{
              background: "#fee2e2",
              color: "#991b1b",
              padding: "12px 16px",
              borderRadius: "0",
              fontSize: "14px",
              marginBottom: "24px",
              textAlign: "center",
              border: "1px solid #fecaca",
            }}
          >
            <i
              className="fa fa-exclamation-circle"
              style={{ marginRight: "8px" }}
            ></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {isRegister && (
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#222222",
                  marginBottom: "8px",
                  letterSpacing: "0.3px",
                }}
              >
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "0",
                  fontSize: "14px",
                  fontFamily: '"Poppins", sans-serif',
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.3s ease",
                }}
                placeholder="Enter your full name"
                required
                onFocus={(e) => {
                  e.target.style.borderColor = "#62B6E8";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#222222",
                marginBottom: "8px",
                letterSpacing: "0.3px",
              }}
            >
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e2e8f0",
                borderRadius: "0",
                fontSize: "14px",
                fontFamily: '"Poppins", sans-serif',
                boxSizing: "border-box",
                outline: "none",
                transition: "all 0.3s ease",
              }}
              placeholder="your.email@example.com"
              required
              onFocus={(e) => {
                e.target.style.borderColor = "#62B6E8";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
              }}
            />
          </div>

          <div style={{ marginBottom: "28px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#222222",
                marginBottom: "8px",
                letterSpacing: "0.3px",
              }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e2e8f0",
                borderRadius: "0",
                fontSize: "14px",
                fontFamily: '"Poppins", sans-serif',
                boxSizing: "border-box",
                outline: "none",
                transition: "all 0.3s ease",
              }}
              placeholder="Enter your password"
              required
              minLength={6}
              onFocus={(e) => {
                e.target.style.borderColor = "#62B6E8";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              background: loading ? "#9ca3af" : "#62B6E8",
              color: "#ffffff",
              border: "none",
              borderRadius: "0",
              fontSize: "15px",
              fontWeight: "600",
              fontFamily: '"Poppins", sans-serif',
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "all 0.3s ease",
              letterSpacing: "0.3px",
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.background = "#2F7FAE";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.background = "#62B6E8";
              }
            }}
          >
            {loading ? (
              <>
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ marginRight: "8px" }}
                ></i>
                {isRegister ? "Creating account..." : "Signing in..."}
              </>
            ) : isRegister ? (
              "Create Account"
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div
          style={{
            marginTop: "24px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "13px",
              color: "#666666",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            {isRegister ? "Already have an account?" : "Don't have an account?"}
          </p>
          <button
            type="button"
            onClick={toggleMode}
            style={{
              background: "none",
              border: "none",
              color: "#62B6E8",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              textDecoration: "underline",
              padding: "0",
            }}
          >
            {isRegister ? "Sign In" : "Create Account"}
          </button>
        </div>

        <div
          style={{
            marginTop: "24px",
            textAlign: "center",
          }}
        >
          <a
            href="/"
            style={{
              fontSize: "13px",
              color: "#666666",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "color 0.3s ease",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#62B6E8")}
            onMouseLeave={(e) => (e.target.style.color = "#666666")}
          >
            <i className="fa fa-arrow-left"></i>
            Back to Website
          </a>
        </div>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
